const PING = 'ping';
const SIGN_UP = 'signUp';
const SIGN_IN = 'signIn';
const SIGN_IN_AS = 'signInAs';
const RESET_PASSWORD = 'resetPassword';
const SET_PASSWORD = 'setPassword';
const ACTIVATE = 'activate';
const SIGN_OUT = 'signOut';
const SIGN_OUT_AS = 'signOutAs';

const SET_ADMIN = 'setAdmin';

const INIT = 'init';
const RESET = 'reset';

const defaultTokens = () => ({
  token_type: null,
  expires_in: 0,
  access_token: null,
  refresh_token: null,
});

const defaultState = () => ({
  hasRegistered: false,
  isAdmin: false,
  tokens: defaultTokens(),
  user: null,
  userToken: null,
  isAuthenticated: false,
  isAuthenticating: false,
  isActivating: false,
  isActivated: false,
  authenticationError: null,
  isFetching: false,
  fetchError: null,
  lastReset: null,
});

export default {
  namespaced: true,

  state: defaultState(),

  /* eslint-disable no-param-reassign */
  mutations: {
    [SET_ADMIN]: (state, isAdmin) => {
      state.isAdmin = isAdmin;
    },

    [`${ACTIVATE}+pending`](state) {
      state.isActivating = true;
    },

    [`${ACTIVATE}+success`](state) {
      state.isActivated = true;
      state.isActivating = false;
    },

    [`${ACTIVATE}+rejected`](state) {
      state.isActivating = false;
    },

    [`${PING}+pending`]: (state) => {
      state.isAuthenticating = true;
      state.isAuthenticated = false;
    },

    [`${PING}+success`]: (state) => {
      state.isAuthenticating = false;
      state.isAuthenticated = true;
    },

    [`${PING}+rejected`]: (state, { payload }) => {
      state.isAuthenticating = false;
      state.authenticationError = payload.message;
    },

    [`${SIGN_UP}+pending`]: (state) => {
      state.hasRegistered = false;
      state.isFetching = true;
    },

    [`${SIGN_UP}+success`]: (state) => {
      state.hasRegistered = true;
      state.isFetching = false;
    },

    [`${SIGN_UP}+rejected`]: (state) => {
      state.isFetching = false;
    },

    [`${SIGN_IN}+pending`]: (state) => {
      state.hasRegistered = false;
      state.isAuthenticated = false;
      state.isAuthenticating = true;
      state.authenticationError = null;
      Object.assign(state.tokens, defaultTokens());
    },

    [`${SIGN_IN}+success`]: (state, { payload }) => {
      state.isAuthenticating = false;
      state.isAuthenticated = true;
      Object.assign(state.tokens, payload);
    },

    [`${SIGN_IN}+rejected`]: (state, { payload }) => {
      state.isAuthenticating = false;
      state.authenticationError = payload.message;
    },

    [`${SIGN_IN_AS}+pending`]: (state) => {
      state.isAuthenticating = true;
      state.userToken = null;
    },

    [`${SIGN_IN_AS}+success`]: (state, { payload }) => {
      state.isAuthenticating = false;
      state.userToken = payload.access_token;
    },

    [`${SIGN_IN_AS}+rejected`]: (state, { payload }) => {
      state.isAuthenticating = false;
      state.authenticationError = payload.message;
    },

    [SIGN_OUT]: (state) => {
      state.isAuthenticated = false;
      state.isAdmin = false;
      Object.assign(state.tokens, defaultTokens());
    },

    [SIGN_OUT_AS]: (state) => {
      state.userToken = null;
      state.user = null;
    },

    [RESET]: (state) => {
      Object.assign(state, defaultState());
    },

    [`${RESET_PASSWORD}+pending`]: (state) => {
      state.isFetching = true;
      state.fetchError = null;
      state.authenticationError = null;
    },

    [`${RESET_PASSWORD}+success`]: (state) => {
      state.isFetching = false;
      state.lastReset = Date.now();
    },

    [`${RESET_PASSWORD}+rejected`]: (state, { payload: error }) => {
      state.isFetching = false;
      state.fetchError = error.message;
    },

    [`${SET_PASSWORD}+pending`]: (state) => {
      state.isFetching = true;
      state.lastReset = null;
      state.fetchError = null;
    },

    [`${SET_PASSWORD}+success`]: (state) => {
      state.isFetching = false;
    },

    [`${SET_PASSWORD}+rejected`]: (state, { payload: error }) => {
      state.isFetching = false;
    },
  },
  /* eslint-disable no-param-reassign */

  actions: {
    [ACTIVATE]({ commit }, { email, token }) {
      return this.$bind({ commit }, ACTIVATE).with(this.$api.post('/users/activate', { email, token }));
    },

    [SET_ADMIN]({ commit }, isAdmin) {
      commit(SET_ADMIN, isAdmin);
    },

    [PING]({ dispatch, commit }) {
      return this.$bind({ commit }, PING).with(
        dispatch('profile/getProfile', null, { root: true }),
      );
    },

    [INIT]({ state, dispatch }) {
      const { tokens } = state;

      if (tokens.access_token) {
        return dispatch(PING);
      }

      return Promise.reject();
    },

    [SIGN_UP](
      store,
      {
        email,
        password,
        passwordConfirmation,
        zipcode,
        houseNumber,
        houseNumberAddition,
        meterNumber,
      },
    ) {
      return this.$bind(store, SIGN_UP).with($api => $api.post(
        '/users/profile',
        {
          email,
          password,
          password_confirmation: passwordConfirmation,
          zipcode,
          house_number: houseNumber,
          house_number_addition: houseNumberAddition,
          meter_number: meterNumber,
        },
        { auth: false },
      ));
    },

    async [SIGN_IN]({ dispatch, commit }, { email, password }) {
      await this.$bind({ commit }, SIGN_IN).with($api => $api.post('/auth/login', { email, password }, { authenticated: false }));

      return dispatch(PING);
    },

    async [SIGN_IN_AS]({ dispatch, commit }, { email, pchn }) {
      await this.$bind({ commit }, SIGN_IN_AS).with($api => $api.post('/admin/impersonate', pchn ? { pchn } : { email }));

      return dispatch(PING);
    },

    [SIGN_OUT]({ commit }) {
      commit(SIGN_OUT);

      commit('account/reset', null, { root: true });
      commit('profile/reset', null, { root: true });
      commit('yields/reset', null, { root: true });
      commit('cache/reset', null, { root: true });
    },

    async [SIGN_OUT_AS]({ commit, dispatch }) {
      commit(SIGN_OUT_AS);
      commit('profile/reset', null, { root: true });

      await dispatch(PING);

      commit('yields/reset', null, { root: true });
      commit('cache/reset', null, { root: true });
    },

    [RESET_PASSWORD](store, { email }) {
      return this.$bind(store, RESET_PASSWORD).with($api => $api.post('/auth/reset-password', { email }, { authenticated: false }));
    },

    [SET_PASSWORD](store, {
      email, token, password, passwordConfirm,
    }) {
      return this.$bind(store, SET_PASSWORD).with($api => $api.post(
        '/auth/set-password',
        {
          email,
          token,
          password,
          password_confirmation: passwordConfirm,
        },
        { authenticated: false },
      ));
    },
  },
};
